
import React, { useState } from 'react';
import { SystemConfig } from '../../types';
import { Globe, Copy, Check, Zap, RefreshCw, Key, Info, Terminal, Server, Hash, Ghost, ShieldAlert, Clock } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { pollCardapioOrders } from '../../services/cardapioWebService';
import { getFunctionsBaseUrl } from '../../services/supabase';

interface Props {
    config: SystemConfig;
    setConfig: (config: SystemConfig) => void;
    storeId?: string;
}

export const IntegrationSubTabPDVs: React.FC<Props> = ({ config, setConfig, storeId }) => {
    const [isTesting, setIsTesting] = useState(false);
    const [copied, setCopied] = useState(false);

    const cw = config?.cardapioWeb || {
        active: false,
        proxyBaseUrl: 'https://integracao.cardapioweb.com',
        merchantId: '',
        apiToken: '',
        scheduledRoutingLeadMinutes: 45,
        enableGhostDispatch: false,
        ghostDispatchMode: 'dynamic',
        ghostDispatchDynamicMinSeconds: 300,
        ghostDispatchDynamicMaxSeconds: 720,
        ghostDispatchBufferMinutes: 5
    };

    const webhookBaseUrl = `${getFunctionsBaseUrl()}/sal-cardapio-webhook`;
    const webhookUrl = `${webhookBaseUrl}?storeId=${storeId || 'default'}`;

    const handleCopyWebhook = () => {
        navigator.clipboard.writeText(webhookUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        toast.success("URL Copiada!");
    };

    const testConnection = async () => {
        if (!cw.apiToken || !cw.merchantId) {
            toast.error("Preencha o Código da Loja e o Token.");
            return;
        }
        setIsTesting(true);
        try {
            const orders = await pollCardapioOrders(cw, undefined, true);
            toast.success(`Conexão OK! ${orders.length} registro(s) retornado(s).`);
        } catch (e: any) {
            toast.error(e?.message ? `Erro ao acessar API: ${e.message}` : "Erro ao acessar API.");
        }
        finally { setTimeout(() => setIsTesting(false), 800); }
    };

    const updateCW = (fields: any) => {
        setConfig({ ...config, cardapioWeb: { ...cw, ...fields } });
    };

    return (
        <div className="space-y-6">
            {/* CARDÁPIO WEB: PAINEL DE CONTROLE */}
            <div className="bg-card-bg rounded-3xl border border-border-dim overflow-hidden shadow-xl">
                <div className="p-6 border-b border-border-dim bg-gradient-to-r from-status-critical/5 to-transparent flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="p-3 rounded-2xl bg-status-critical/10 text-status-critical shadow-inner"><Globe className="w-6 h-6" /></div>
                        <div>
                            <h3 className="text-base font-black text-text-primary uppercase tracking-widest">Cardápio Web Integration</h3>
                            <p className="text-xs text-text-muted uppercase font-bold mt-1">Status: {cw.active ? 'Monitoramento Ativo' : 'Desconectado'}</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => updateCW({ active: !cw.active })}
                        className={`px-5 py-2.5 rounded-xl text-xs font-black uppercase transition-all shadow-lg ${cw.active ? 'bg-status-critical text-badge-text-inv' : 'bg-bg-main/50 text-text-muted border border-border-dim'}`}
                    >
                        {cw.active ? 'Pausar Sincronia' : 'Ativar Agora'}
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    <div className="space-y-3 bg-bg-main/50 p-4 rounded-2xl border border-border-dim shadow-sm">
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-black text-text-muted uppercase flex items-center gap-2"><Zap className="w-3.5 h-3.5 text-sal-cyber-teal" /> Webhook Instantâneo (Cloud Functions V2)</span>
                            <span className="text-xs font-black text-sal-cyber-teal bg-sal-cyber-teal/10 px-2 py-0.5 rounded-full uppercase">Recomendado</span>
                        </div>
                        <div className="flex gap-2">
                            <div className="flex-1 bg-input-bg p-2.5 rounded-xl border border-input-border font-mono text-xs text-text-primary truncate">{webhookUrl}</div>
                            <button type="button" onClick={handleCopyWebhook} className="px-4 bg-sal-cyber-teal text-badge-text-inv rounded-xl font-black text-xs uppercase hover:brightness-110 transition-all">{copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}</button>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label htmlFor="cw-merchant-id" className="text-xs font-black text-text-muted uppercase flex items-center gap-2"><Hash className="w-3.5 h-3.5" /> Código da Loja (Merchant ID)</label>
                            <input id="cw-merchant-id" type="text" value={cw.merchantId || ''} onChange={e => updateCW({ merchantId: e.target.value })} placeholder="Ex: 7637457" className="w-full bg-input-bg border border-input-border p-3 rounded-2xl text-sm text-text-primary focus:border-sal-cyber-teal transition-all outline-none" />
                        </div>
                        <div className="space-y-2">
                            <label htmlFor="cw-api-token" className="text-xs font-black text-text-muted uppercase flex items-center gap-2"><Key className="w-3.5 h-3.5" /> API Token (X-API-KEY)</label>
                            <input id="cw-api-token" type="password" value={cw.apiToken || ''} onChange={e => updateCW({ apiToken: e.target.value })} placeholder="Insira o Token de Integração" className="w-full bg-input-bg border border-input-border p-3 rounded-2xl text-sm text-text-primary focus:border-sal-cyber-teal transition-all outline-none" />
                        </div>
                    </div>

                    <div className="space-y-3 bg-bg-main/30 p-4 rounded-2xl border border-border-dim shadow-sm">
                        <div className="flex items-center justify-between text-xs font-black uppercase tracking-widest text-text-muted">
                            <span>Pedidos Agendados</span>
                            <span className="text-status-info font-mono">Libera em {cw.scheduledRoutingLeadMinutes || 45} min</span>
                        </div>
                        <div className="space-y-2">
                            <label htmlFor="cw-scheduled-lead" className="text-xs font-bold text-text-muted uppercase ml-1">Janela de Entrada no SmartSplit (min)</label>
                            <input
                                id="cw-scheduled-lead"
                                type="range"
                                min="10"
                                max="180"
                                step="5"
                                value={cw.scheduledRoutingLeadMinutes || 45}
                                onChange={e => updateCW({ scheduledRoutingLeadMinutes: parseInt(e.target.value) })}
                                className="w-full h-1.5 bg-border-strong rounded-lg appearance-none cursor-pointer accent-status-info"
                            />
                        </div>
                        <p className="text-xs text-text-muted italic">Pedidos com status agendado so entram na distribuição automatica dentro desta janela.</p>
                    </div>

                    {/* v44.7: SMART GHOST DISPATCH SECTION */}
                    <div className="mt-8 bg-status-info/5 border border-status-info/20 rounded-[2rem] overflow-hidden shadow-sm">
                        <div className="p-6 bg-status-info/5 border-b border-status-info/10 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="p-2.5 bg-status-info/10 text-status-info rounded-xl shadow-inner"><Ghost className="w-5 h-5" /></div>
                                <div>
                                    <h4 className="text-sm font-black text-text-primary uppercase tracking-widest">Smart Ghost Dispatch</h4>
                                    <p className="text-xs text-status-info/80 font-bold uppercase tracking-tighter">Ranking iFood Strategy Engine</p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => updateCW({ enableGhostDispatch: !cw.enableGhostDispatch })}
                                className={`relative w-12 h-6 rounded-full transition-all ${cw.enableGhostDispatch ? 'bg-status-info' : 'bg-border-strong'}`}
                            >
                                <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform ${cw.enableGhostDispatch ? 'translate-x-6' : 'translate-x-0'}`} />
                            </button>
                        </div>

                        {cw.enableGhostDispatch && (
                            <div className="p-6 space-y-6 animate-in slide-in-from-top-4 duration-300">
                                <div className="flex gap-2 p-1 bg-bg-main/50 rounded-xl border border-border-dim shadow-inner">
                                    <button type="button" onClick={() => updateCW({ ghostDispatchMode: 'dynamic' })} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-black uppercase transition-all ${cw.ghostDispatchMode === 'dynamic' ? 'bg-status-info text-badge-text-inv shadow-md' : 'text-text-muted hover:text-text-primary'}`}>
                                        <ShieldAlert className="w-3.5 h-3.5" /> Modo Dinâmico (Anti-Robô)
                                    </button>
                                    <button type="button" onClick={() => updateCW({ ghostDispatchMode: 'predictive' })} className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-black uppercase transition-all ${cw.ghostDispatchMode === 'predictive' ? 'bg-status-info text-badge-text-inv shadow-md' : 'text-text-muted hover:text-text-primary'}`}>
                                        <Clock className="w-3.5 h-3.5" /> Modo preditivo (No momento certo)
                                    </button>
                                </div>

                                {cw.ghostDispatchMode === 'dynamic' ? (
                                    <div className="space-y-4 bg-bg-main/30 p-5 rounded-2xl border border-border-dim shadow-sm">
                                        <div className="flex justify-between items-center text-xs font-black uppercase tracking-widest text-text-muted">
                                            <span>Janela de Despacho Aleatório</span>
                                            <span className="text-status-info font-mono">{(cw.ghostDispatchDynamicMinSeconds / 60).toFixed(0)} - {(cw.ghostDispatchDynamicMaxSeconds / 60).toFixed(0)} min</span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <label htmlFor="cw-ghost-min" className="text-xs font-bold text-text-muted uppercase ml-1">Mínimo (Segundos)</label>
                                                <input id="cw-ghost-min" type="number" step="60" value={cw.ghostDispatchDynamicMinSeconds} onChange={e => updateCW({ ghostDispatchDynamicMinSeconds: parseInt(e.target.value) })} className="w-full bg-input-bg border border-input-border p-2.5 rounded-xl text-sm text-text-primary outline-none focus:border-status-info" />
                                            </div>
                                            <div className="space-y-2">
                                                <label htmlFor="cw-ghost-max" className="text-xs font-bold text-text-muted uppercase ml-1">Máximo (Segundos)</label>
                                                <input id="cw-ghost-max" type="number" step="60" value={cw.ghostDispatchDynamicMaxSeconds} onChange={e => updateCW({ ghostDispatchDynamicMaxSeconds: parseInt(e.target.value) })} className="w-full bg-input-bg border border-input-border p-2.5 rounded-xl text-sm text-text-primary outline-none focus:border-status-info" />
                                            </div>
                                        </div>
                                        <p className="text-xs text-text-muted italic">Simulação de tempo de reação humano.</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4 bg-bg-main/30 p-5 rounded-2xl border border-border-dim shadow-sm">
                                        <div className="flex justify-between items-center text-xs font-black uppercase tracking-widest text-text-muted">
                                            <span>Antecipação Just-in-Time</span>
                                            <span className="text-status-info font-mono">{cw.ghostDispatchBufferMinutes} min antes do Pronto</span>
                                        </div>
                                        <div className="space-y-2">
                                            <label htmlFor="cw-ghost-buffer" className="text-xs font-bold text-text-muted uppercase ml-1">Buffer de Antecipação (Minutos)</label>
                                            <input id="cw-ghost-buffer" type="range" min="1" max="15" value={cw.ghostDispatchBufferMinutes} onChange={e => updateCW({ ghostDispatchBufferMinutes: parseInt(e.target.value) })} className="w-full h-1.5 bg-border-strong rounded-lg appearance-none cursor-pointer accent-status-info" />
                                        </div>
                                        <p className="text-xs text-text-muted italic">Sincroniza o status 'Saiu para Entrega' com a previsão real.</p>
                                    </div>
                                )}

                                <div className="p-4 bg-status-info/10 rounded-2xl border border-status-info/20 flex gap-3 shadow-inner">
                                    <Info className="w-4 h-4 text-status-info shrink-0 mt-0.5" />
                                    <p className="text-xs text-text-muted font-medium leading-relaxed uppercase">
                                        <strong className="text-status-info">Estratégia:</strong> O SAL manterá o pedido em triagem interna para otimizar rotas.
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="p-4 rounded-2xl bg-bg-main/30 border border-border-dim flex items-center justify-between shadow-sm">
                        <div className="flex items-center gap-3">
                            <Server className="w-4 h-4 text-text-muted" />
                            <div>
                                <span className="text-xs font-black text-text-muted uppercase block">Ambiente</span>
                                <span className="text-xs text-text-muted font-bold uppercase">{cw.proxyBaseUrl.includes('sandbox') ? 'Sandbox / Testes' : 'Produção / Real'}</span>
                            </div>
                        </div>
                        <select value={cw.proxyBaseUrl} onChange={e => updateCW({ proxyBaseUrl: e.target.value })} className="bg-card-bg border border-border-dim text-xs font-black text-text-primary px-3 py-1.5 rounded-lg outline-none cursor-pointer shadow-sm">
                            <option value="https://integracao.cardapioweb.com">Produção</option>
                            <option value="https://integracao.sandbox.cardapioweb.com">Sandbox</option>
                        </select>
                    </div>

                    <div className="pt-2">
                        <button type="button" onClick={testConnection} disabled={isTesting} className="w-full py-3.5 bg-card-bg hover:bg-bg-main border border-border-dim rounded-2xl text-xs font-black uppercase text-text-muted transition-all flex items-center justify-center gap-3 active:scale-[0.98] shadow-sm">
                            {isTesting ? <RefreshCw className="w-4 h-4 animate-spin text-sal-cyber-teal" /> : <Terminal className="w-4 h-4" />}
                            {isTesting ? 'Validando Protocolos...' : 'Testar Conexão com Cardápio Web'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
